import { jsx, jsxs } from 'react/jsx-runtime';
import { ErrorMessage } from '@promo-platform/error-message';
import { QuizAnswers } from '@promo-platform/quiz-answers';
import { Skeleton, SkeletonTypes } from '@promo-platform/skeleton';
import { SkeletonBlock } from '@promo-platform/skeleton-block';
import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import axios from 'axios';

function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

const sendQuizData = ({ url, id, body }) => __awaiter(void 0, void 0, void 0, function* () {
    let response;
    try {
        const { data } = yield axios.post(`${url}/${id}/response`, body);
        response = data;
    }
    catch (error) {
        console.error(error);
    }
    return response;
});

const DEFAULT_ERROR_MESSAGE = 'Oops… something went wrong, please try again later.';

const useFetchData = (url) => {
    const [data, setData] = useState();
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const { data: response } = yield axios.get(url);
                setData(response);
            }
            catch (error) {
                console.error(error);
            }
            setIsLoading(false);
        });
        fetchData();
    }, []);
    return {
        data,
        isLoading,
    };
};

const SeparatorIcon = () => (jsx("svg", Object.assign({ xmlns: "http://www.w3.org/2000/svg", width: "30.828", height: "54.828", viewBox: "0 0 30.828 54.828" }, { children: jsxs("g", Object.assign({ id: "Group_1175", "data-name": "Group 1175", transform: "translate(-191.586 -382.336)" }, { children: [jsxs("g", Object.assign({ id: "Group_1143", "data-name": "Group 1143", transform: "translate(-469.5 -269.75)" }, { children: [jsx("line", { id: "Line_32", "data-name": "Line 32", x2: "14", y2: "14", transform: "translate(662.5 653.5)", fill: "none", stroke: "#707070", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_33", "data-name": "Line 33", y1: "14", x2: "14", transform: "translate(676.5 653.5)", fill: "none", stroke: "#707070", strokeLinecap: "round", strokeWidth: "2" })] })), jsxs("g", Object.assign({ id: "Group_1144", "data-name": "Group 1144", transform: "translate(-469.5 -264.75)" }, { children: [jsx("line", { id: "Line_35", "data-name": "Line 35", x2: "14", y2: "14", transform: "translate(662.5 667.5)", fill: "none", stroke: "#d4d4d4", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_34", "data-name": "Line 34", y1: "14", x2: "14", transform: "translate(676.5 667.5)", fill: "none", stroke: "#d4d4d4", strokeLinecap: "round", strokeWidth: "2" })] })), jsxs("g", Object.assign({ id: "Group_1145", "data-name": "Group 1145", transform: "translate(-469.5 -259.75)" }, { children: [jsx("line", { id: "Line_37", "data-name": "Line 37", x2: "14", y2: "14", transform: "translate(662.5 681.5)", fill: "none", stroke: "#f2f2f2", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_36", "data-name": "Line 36", y1: "14", x2: "14", transform: "translate(676.5 681.5)", fill: "none", stroke: "#f2f2f2", strokeLinecap: "round", strokeWidth: "2" })] }))] })) })));

const prepareBody = (quizFormData, questions) => ({
    questionResponses: questions.map((question) => {
        var _a;
        const quizDataValue = quizFormData[question.alias];
        return {
            questionId: question.id,
            answers: Array.isArray(quizDataValue)
                ? quizDataValue
                    .map((alias) => { var _a; return (_a = question.answers.find((answer) => answer.alias === alias)) === null || _a === void 0 ? void 0 : _a.id; })
                    .filter(Boolean) || []
                : [
                    (_a = question.answers.find((answer) => answer.alias === quizDataValue)) === null || _a === void 0 ? void 0 : _a.id,
                ].filter(Boolean),
        };
    }),
});
const prepareOptions = (answers) => answers.map(({ alias: answerAlias, title: answerTitle }) => ({
    value: answerAlias,
    label: answerTitle,
    isSelected: false,
}));

const PromoQuiz = ({ url, id, onSuccessCallback }) => {
    const { data } = useFetchData(`${url}/${id}`);
    const [quizError, setQuizError] = useState('');
    const methods = useForm();
    const onSuccess = (redirectUrl) => {
        if (onSuccessCallback) {
            onSuccessCallback(redirectUrl);
        }
        else {
            window.open(redirectUrl, '_self');
        }
    };
    const onSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        if (data) {
            const body = prepareBody(values, data.sections[0].questions);
            try {
                yield sendQuizData({ url, id, body });
                onSuccess(data.resultValue);
            }
            catch (_a) {
                setQuizError(DEFAULT_ERROR_MESSAGE);
            }
        }
    });
    return (jsx("section", Object.assign({ className: "promo-quiz" }, { children: jsx(FormProvider, Object.assign({}, methods, { children: jsxs("form", Object.assign({ onSubmit: methods.handleSubmit(onSubmit) }, { children: [jsx("div", { className: "promo-quiz__header" }), jsxs("div", Object.assign({ className: "promo-quiz__content" }, { children: [data ? (jsx("h2", Object.assign({ className: "promo-quiz__title" }, { children: data.title }))) : (jsx(Skeleton, { type: SkeletonTypes.Title })), data ? (jsx("p", Object.assign({ className: "promo-quiz__description" }, { children: data.description }))) : (jsx(Skeleton, { type: SkeletonTypes.Text, lines: 2 })), jsx("div", Object.assign({ className: "promo-quiz__separator" }, { children: jsx(SeparatorIcon, {}) })), data ? (data.sections[0].questions.map(({ alias, id: questionId, title, questionType, answers, description, }) => (jsx(QuizAnswers, { alias: alias, id: questionId, label: title, questionType: questionType, options: prepareOptions(answers), description: description }, questionId)))) : (jsx(SkeletonBlock, { amount: 4 })), jsx(ErrorMessage, { message: quizError }), data ? (jsx("div", Object.assign({ className: "promo-quiz__submit-button" }, { children: jsx("button", Object.assign({ type: "submit", disabled: methods.formState.isSubmitting, "aria-label": data.submitButtonDescription || data.submitButtonText }, { children: data.submitButtonText })) }))) : (jsx(Skeleton, { type: SkeletonTypes.Title }))] })), jsx("div", { className: "promo-quiz__footer" })] })) })) })));
};

export { PromoQuiz as default };
